import { SlidersHorizontal, X } from "lucide-react";
import { SearchInput } from "@/components/shared/search-input";
import {
  activeFilterCount,
  POINTS_MIN,
  RATING_MAX,
  RATING_MIN,
  RESERVATION_FILTER_OPTIONS,
  type ResourceFilterState,
} from "@/lib/resource-filters";
import { FilterPopover } from "./FilterPopover";
import {
  ChipRow,
  ChipToggle,
  FilterSection,
  LevelSelect,
  PointsRange,
  SearchableMultiSelect,
} from "./filter-controls";

export interface ResourceFilterOptions {
  disciplines: Array<{ code: string; name: string }>;
  constraints: Array<{ id: string; name: string }>;
  solutions: Array<{ id: string; name: string }>;
  accounts: Array<{ id: string; name: string }>;
  pointsCeiling: number;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

export function ResourcesFilterBar({
  state,
  setState,
  reset,
  options,
}: {
  state: ResourceFilterState;
  setState: (n: ResourceFilterState) => void;
  reset: () => void;
  options: ResourceFilterOptions;
}) {
  const count = activeFilterCount(state);
  const constraintName = (id: string) => options.constraints.find((c) => c.id === id)?.name ?? id;
  const solutionName = (id: string) => options.solutions.find((s) => s.id === id)?.name ?? id;
  const accountName = (id: string) => options.accounts.find((a) => a.id === id)?.name ?? id;

  const chips: Chip[] = [];
  const reservationFilter = RESERVATION_FILTER_OPTIONS.find(
    (o) => o.value === state.reservationFilter,
  );
  if (reservationFilter)
    chips.push({
      key: "resv",
      label: reservationFilter.label,
      clear: () => setState({ ...state, reservationFilter: null }),
    });
  for (const d of state.disciplines)
    chips.push({
      key: `disc:${d}`,
      label: d,
      clear: () => setState({ ...state, disciplines: state.disciplines.filter((v) => v !== d) }),
    });
  for (const c of state.constraints)
    chips.push({
      key: `con:${c}`,
      label: `${constraintName(c)} ${state.constraintMin}+`,
      clear: () => setState({ ...state, constraints: state.constraints.filter((v) => v !== c) }),
    });
  if (state.pointsMin !== POINTS_MIN || state.pointsMax != null)
    chips.push({
      key: "load",
      label: `Load ${state.pointsMin}–${state.pointsMax ?? "∞"} pts`,
      clear: () => setState({ ...state, pointsMin: POINTS_MIN, pointsMax: null }),
    });
  for (const s of state.solutions)
    chips.push({
      key: `sol:${s}`,
      label: solutionName(s),
      clear: () => setState({ ...state, solutions: state.solutions.filter((v) => v !== s) }),
    });
  for (const a of state.accounts)
    chips.push({
      key: `acct:${a}`,
      label: accountName(a),
      clear: () => setState({ ...state, accounts: state.accounts.filter((v) => v !== a) }),
    });

  return (
    <div>
      <div className="flex items-center justify-end gap-2">
        <SearchInput
          value={state.search}
          onValueChange={(v) => setState({ ...state, search: v })}
          placeholder="Search resources…"
          className="w-64"
        />
        <FilterPopover
          trigger={() => (
            <>
              <SlidersHorizontal className="size-4" />
              <span>Filters{count > 0 ? ` (${count})` : ""}</span>
            </>
          )}
        >
          {() => (
            <div>
              <FilterSection label="Reservation">
                <ChipRow>
                  {RESERVATION_FILTER_OPTIONS.map((o) => (
                    <ChipToggle
                      key={o.value}
                      active={state.reservationFilter === o.value}
                      onToggle={() =>
                        setState({
                          ...state,
                          reservationFilter: state.reservationFilter === o.value ? null : o.value,
                        })
                      }
                    >
                      {o.label}
                    </ChipToggle>
                  ))}
                </ChipRow>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Reserved means reserved for an Account.
                </p>
              </FilterSection>

              {options.disciplines.length > 0 && (
                <FilterSection label="Discipline (any)">
                  <ChipRow>
                    {options.disciplines.map((d) => (
                      <ChipToggle
                        key={d.code}
                        active={state.disciplines.includes(d.code)}
                        onToggle={() =>
                          setState({ ...state, disciplines: toggle(state.disciplines, d.code) })
                        }
                      >
                        {d.code}
                      </ChipToggle>
                    ))}
                  </ChipRow>
                </FilterSection>
              )}

              <FilterSection label="Constraint (holds all selected)">
                <div className="mb-2">
                  <LevelSelect
                    label="Min rating"
                    value={state.constraintMin}
                    min={RATING_MIN}
                    max={RATING_MAX}
                    onChange={(v) => setState({ ...state, constraintMin: v })}
                  />
                </div>
                <SearchableMultiSelect
                  options={options.constraints}
                  selected={state.constraints}
                  placeholder="Search constraints…"
                  onToggle={(id) =>
                    setState({ ...state, constraints: toggle(state.constraints, id) })
                  }
                />
              </FilterSection>

              <FilterSection label="Load (points)">
                <PointsRange
                  min={state.pointsMin}
                  max={state.pointsMax}
                  ceiling={options.pointsCeiling}
                  onChange={(min, max) => setState({ ...state, pointsMin: min, pointsMax: max })}
                />
              </FilterSection>

              {options.solutions.length > 0 && (
                <FilterSection label="Solution (any)">
                  <SearchableMultiSelect
                    options={options.solutions}
                    selected={state.solutions}
                    placeholder="Search solutions…"
                    onToggle={(id) =>
                      setState({ ...state, solutions: toggle(state.solutions, id) })
                    }
                  />
                </FilterSection>
              )}

              {options.accounts.length > 0 && (
                <FilterSection label="Account (any)">
                  <SearchableMultiSelect
                    options={options.accounts}
                    selected={state.accounts}
                    placeholder="Search accounts…"
                    onToggle={(id) => setState({ ...state, accounts: toggle(state.accounts, id) })}
                  />
                </FilterSection>
              )}
            </div>
          )}
        </FilterPopover>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-1.5">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
            >
              {c.label}
              <button
                type="button"
                onClick={c.clear}
                aria-label={`Remove ${c.label}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={reset}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
